/* =====================================================================
 * MediaViews — 미디어 블록 React NodeView 컴포넌트
 *   - ImageBlockView, VideoBlockView, AudioBlockView,
 *     FileBlockView, BookmarkBlockView
 *   - useAuthenticatedMedia 훅 (서버 파일 fetch → blob URL)
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import { NodeSelection, Selection } from '@tiptap/pm/state';
import {
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  File as FileIconLucide,
  FileCode2,
  FileArchive,
} from 'lucide-react';
import { previewBookmark } from '@auw/api-client';
import { API_BASE_URL, authFetch } from '@auw/api-client';
import { MediaPlaceholder } from './MediaPlaceholder';
import {
  ResizableContainer,
  ResizableInner,
  ResizeHandle,
  ResizeWidthLabel,
  MediaBlockOuter,
  MediaBlockHandleWrap,
  MediaHandleBtn,
  AudioWrap,
  AudioIcon,
  AudioBody,
  AudioFileName,
  FileCardWrap,
  FileIcon,
  FileInfo,
  FileName,
  FileMeta,
  FileDownloadBtn,
  BookmarkCardWrap,
  BookmarkBody,
  BookmarkTextArea,
  BookmarkTitle,
  BookmarkDescription,
  BookmarkMeta,
  BookmarkUrl,
  BookmarkFavicon,
  BookmarkImage,
  MediaSpinner,
} from './media.styles';

/* ------------------------------------------------------------------ */
/* useAuthenticatedMedia — 서버 파일을 인증된 fetch → blob URL로 변환    */
/* ------------------------------------------------------------------ */

export function useAuthenticatedMedia(serverPath: string | null): {
  blobUrl: string | null;
  loading: boolean;
} {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  /*
   * serverPath 가 주어진 상태로 훅이 처음 실행되면 첫 렌더에서
   * loading=false, blobUrl=null 상태가 잠시 유지된다.
   * 이때 NodeView 가 `!displaySrc && !loading` 으로 Placeholder 를 그리면
   * "업로드 직후 이미지가 잠깐 업로드 위젯으로 보이는" 깜빡임이 발생한다.
   * initial state 에서부터 serverPath 가 있으면 loading=true 로 설정해
   * 첫 렌더부터 로딩 스피너로 렌더되게 한다.
   */
  const [loading, setLoading] = useState(Boolean(serverPath));
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!serverPath) {
      setBlobUrl(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    /*
     * authFetch 를 쓰면 401 이 떴을 때 refresh-token 로 자동 재인증 후 1회
     * 재시도한다. raw fetch + getHeaders 조합은 토큰 만료 시점에 바로 실패해서
     * 이미지가 영원히 "로딩 중..." 에 머무는 버그의 원인이 된다.
     */
    authFetch(`${API_BASE_URL}${serverPath}`)
      .then((res) => {
        if (!res.ok) throw new Error(`File fetch failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setBlobUrl(url);
      })
      .catch((err) => {
        if (!cancelled) console.error('[useAuthenticatedMedia]', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [serverPath]);

  return { blobUrl, loading };
}

/* ------------------------------------------------------------------ */
/* 유틸: 파일 크기 포맷                                                 */
/* ------------------------------------------------------------------ */

function formatSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface FileTypeInfo {
  icon: React.ReactNode;
  color: string;
  bg: string;
  ext: string;
}

/* MessageBubble와 통일된 MIME → 확장자 폴백 */
const MIME_TO_EXT: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/csv': 'csv',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'text/plain': 'txt',
  'text/markdown': 'md',
  'application/json': 'json',
  'application/zip': 'zip',
  'application/x-tar': 'tar',
  'application/gzip': 'gz',
};

function getFileTypeInfo(name: string | null, mimeType: string | null): FileTypeInfo {
  const fileName = name || '';
  const parts = fileName.split('.');
  let ext = parts.length > 1 ? (parts.pop()?.toLowerCase() || '') : '';
  if (ext.length > 10) ext = '';
  if (!ext && mimeType) {
    ext = MIME_TO_EXT[mimeType.split(';')[0].trim().toLowerCase()] || '';
  }

  if ((mimeType || '').startsWith('image/')) {
    return {
      icon: <ImageIcon size={18} />,
      color: '#8B5CF6',
      bg: 'rgba(139,92,246,0.1)',
      ext: ext.toUpperCase() || 'IMG',
    };
  }

  switch (ext) {
    case 'pdf':
      return { icon: <FileText size={18} />, color: '#EF4444', bg: 'rgba(239,68,68,0.1)', ext: 'PDF' };
    case 'xls':
    case 'xlsx':
    case 'csv':
      return { icon: <FileSpreadsheet size={18} />, color: '#22C55E', bg: 'rgba(34,197,94,0.1)', ext: ext.toUpperCase() || 'CSV' };
    case 'doc':
    case 'docx':
      return { icon: <FileText size={18} />, color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', ext: ext.toUpperCase() };
    case 'ppt':
    case 'pptx':
      return { icon: <FileText size={18} />, color: '#F97316', bg: 'rgba(249,115,22,0.1)', ext: ext.toUpperCase() };
    case 'txt':
    case 'md':
      return { icon: <FileText size={18} />, color: '#6B7280', bg: 'rgba(107,114,128,0.1)', ext: ext.toUpperCase() };
    case 'json':
    case 'xml':
    case 'yaml':
    case 'yml':
    case 'py':
    case 'js':
    case 'ts':
    case 'html':
    case 'css':
      return { icon: <FileCode2 size={18} />, color: '#6366F1', bg: 'rgba(99,102,241,0.1)', ext: ext.toUpperCase() };
    case 'zip':
    case 'tar':
    case 'gz':
    case 'rar':
    case '7z':
      return { icon: <FileArchive size={18} />, color: '#A16207', bg: 'rgba(161,98,7,0.1)', ext: ext.toUpperCase() };
    default:
      return { icon: <FileIconLucide size={18} />, color: '#6B7280', bg: 'rgba(107,114,128,0.1)', ext: ext.toUpperCase() || 'FILE' };
  }
}

/* ------------------------------------------------------------------ */
/* 유틸: YouTube / Vimeo 등 임베드 URL 변환                             */
/* ------------------------------------------------------------------ */

function getEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);

    // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
    if (u.hostname.includes('youtube.com') || u.hostname.includes('youtu.be')) {
      let videoId: string | null = null;
      if (u.hostname.includes('youtu.be')) {
        videoId = u.pathname.slice(1);
      } else if (u.pathname.startsWith('/embed/')) {
        return url; // already embed URL
      } else {
        videoId = u.searchParams.get('v');
      }
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    }

    // Vimeo: vimeo.com/ID
    if (u.hostname.includes('vimeo.com')) {
      const match = u.pathname.match(/^\/(\d+)/);
      if (match) return `https://player.vimeo.com/video/${match[1]}`;
    }
  } catch { /* not a valid URL */ }
  return null;
}

/* ================================================================== */
/* MediaBlockHandle — atom 블록용 + / grip 핸들 (호버 시 왼쪽 표시)      */
/* ================================================================== */

interface MediaBlockHandleProps {
  editor: Editor;
  getPos: () => number | undefined;
}

export const MediaBlockHandle: React.FC<MediaBlockHandleProps> = ({ editor, getPos }) => {
  const handlePlusClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const pos = getPos();
      if (pos == null) return;
      const node = editor.state.doc.nodeAt(pos);
      const nodeSize = node?.nodeSize ?? 1;
      const endOfBlock = pos + nodeSize;
      const { tr } = editor.state;
      const newParagraph = editor.state.schema.nodes.paragraph.create();
      tr.insert(endOfBlock, newParagraph);
      tr.setSelection(Selection.near(tr.doc.resolve(endOfBlock + 1)));
      editor.view.dispatch(tr);
      editor.view.focus();

      requestAnimationFrame(() => {
        try {
          const cursorPos = endOfBlock + 1;
          window.dispatchEvent(
            new CustomEvent('media-block-open-slash', { detail: { pos: cursorPos } }),
          );
        } catch { /* 방어 */ }
      });
    },
    [editor, getPos],
  );

  const handleGripMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const pos = getPos();
      if (pos == null) return;
      const tr = editor.state.tr.setSelection(
        NodeSelection.create(editor.state.doc, pos),
      );
      editor.view.dispatch(tr);
      editor.view.focus();
    },
    [editor, getPos],
  );

  return (
    <MediaBlockHandleWrap className="media-block-handle" contentEditable={false}>
      <MediaHandleBtn onClick={handlePlusClick} title="블록 추가">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <line x1="6" y1="1" x2="6" y2="11" />
          <line x1="1" y1="6" x2="11" y2="6" />
        </svg>
      </MediaHandleBtn>
      <MediaHandleBtn
        data-drag-handle
        onMouseDown={handleGripMouseDown}
        style={{ cursor: 'grab' }}
        title="드래그하여 이동"
      >
        <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
          <circle cx="2" cy="2" r="1.5" />
          <circle cx="8" cy="2" r="1.5" />
          <circle cx="2" cy="8" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="2" cy="14" r="1.5" />
          <circle cx="8" cy="14" r="1.5" />
        </svg>
      </MediaHandleBtn>
    </MediaBlockHandleWrap>
  );
};

/* ================================================================== */
/* ResizableMedia — 노션 스타일 양쪽 핸들로 리사이즈 가능한 래퍼         */
/* ================================================================== */

const MIN_WIDTH_PX = 60;

interface ResizableMediaProps {
  width: number | null;
  onWidthChange: (pct: number) => void;
  children: React.ReactNode;
}

export const ResizableMedia: React.FC<ResizableMediaProps> = ({ width, onWidthChange, children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [liveWidth, setLiveWidth] = useState<number | null>(null);
  const dragRef = useRef<{
    side: 'left' | 'right';
    startX: number;
    startWidthPx: number;
    containerWidth: number;
  } | null>(null);

  const displayPct = liveWidth ?? width ?? 100;

  const handleMouseDown = useCallback(
    (side: 'left' | 'right', e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.getBoundingClientRect().width;
      const currentPct = width ?? 100;
      const startWidthPx = (currentPct / 100) * containerWidth;

      dragRef.current = {
        side,
        startX: e.clientX,
        startWidthPx,
        containerWidth,
      };
      setDragging(true);
      setLiveWidth(currentPct);
    },
    [width],
  );

  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;

      const dx = e.clientX - d.startX;
      const delta = d.side === 'right' ? dx : -dx;
      const newWidthPx = Math.max(MIN_WIDTH_PX, d.startWidthPx + delta * 2);
      const newPct = Math.min(100, Math.round((newWidthPx / d.containerWidth) * 100));
      setLiveWidth(newPct);
    };

    const handleMouseUp = () => {
      setDragging(false);
      if (liveWidth !== null) {
        onWidthChange(liveWidth);
      }
      setLiveWidth(null);
      dragRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, liveWidth, onWidthChange]);

  return (
    <ResizableContainer ref={containerRef}>
      <ResizableInner style={{ width: `${displayPct}%` }}>
        {children}
        <ResizeHandle
          className="resize-handle"
          $side="left"
          onMouseDown={(e) => handleMouseDown('left', e)}
        />
        <ResizeHandle
          className="resize-handle"
          $side="right"
          onMouseDown={(e) => handleMouseDown('right', e)}
        />
        {dragging && (
          <ResizeWidthLabel>{displayPct}%</ResizeWidthLabel>
        )}
      </ResizableInner>
    </ResizableContainer>
  );
};

/* ================================================================== */
/* ImageBlockView                                                      */
/* ================================================================== */

export const ImageBlockView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
  updateAttributes,
  selected,
}) => {
  const { src, serverPath, width } = node.attrs;
  const { blobUrl, loading } = useAuthenticatedMedia(serverPath || null);

  const displaySrc = useMemo(() => {
    if (src) return src;
    return blobUrl;
  }, [src, blobUrl]);

  const handleUpload = useCallback(
    (info: {
      serverPath: string;
      sessionId: string;
      messageId: string;
      fileName: string;
      fileSize: number;
      mimeType: string;
    }) => {
      updateAttributes({
        serverPath: info.serverPath,
        sessionId: info.sessionId,
        messageId: info.messageId,
        fileName: info.fileName,
        fileSize: info.fileSize,
        mimeType: info.mimeType,
      });
    },
    [updateAttributes],
  );

  const handleEmbed = useCallback(
    (url: string) => {
      updateAttributes({ src: url });
    },
    [updateAttributes],
  );

  const handleWidthChange = useCallback(
    (pct: number) => {
      updateAttributes({ width: pct });
    },
    [updateAttributes],
  );

  if (!displaySrc && !loading) {
    return (
      <NodeViewWrapper data-selected={selected || undefined}>
        <MediaBlockOuter>
          <MediaBlockHandle editor={editor} getPos={getPos} />
          <MediaPlaceholder type="image" onUpload={handleUpload} onEmbed={handleEmbed} />
        </MediaBlockOuter>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper data-selected={selected || undefined}>
      <MediaBlockOuter>
        <MediaBlockHandle editor={editor} getPos={getPos} />
        {loading ? (
          <MediaSpinner>이미지 로딩 중...</MediaSpinner>
        ) : (
          <ResizableMedia width={width} onWidthChange={handleWidthChange}>
            <img src={displaySrc!} alt={node.attrs.fileName || ''} />
          </ResizableMedia>
        )}
      </MediaBlockOuter>
    </NodeViewWrapper>
  );
};

/* ================================================================== */
/* VideoBlockView                                                      */
/* ================================================================== */

export const VideoBlockView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
  updateAttributes,
  selected,
}) => {
  const { src, serverPath, width } = node.attrs;
  const { blobUrl, loading } = useAuthenticatedMedia(serverPath || null);
  const displaySrc = src || blobUrl;
  const embedUrl = useMemo(() => (src ? getEmbedUrl(src) : null), [src]);

  const handleUpload = useCallback(
    (info: {
      serverPath: string;
      sessionId: string;
      messageId: string;
      fileName: string;
      fileSize: number;
      mimeType: string;
    }) => {
      updateAttributes({
        serverPath: info.serverPath,
        sessionId: info.sessionId,
        messageId: info.messageId,
        fileName: info.fileName,
        fileSize: info.fileSize,
        mimeType: info.mimeType,
      });
    },
    [updateAttributes],
  );

  const handleEmbed = useCallback(
    (url: string) => {
      updateAttributes({ src: url });
    },
    [updateAttributes],
  );

  const handleWidthChange = useCallback(
    (pct: number) => {
      updateAttributes({ width: pct });
    },
    [updateAttributes],
  );

  if (!displaySrc && !loading) {
    return (
      <NodeViewWrapper data-selected={selected || undefined}>
        <MediaBlockOuter>
          <MediaBlockHandle editor={editor} getPos={getPos} />
          <MediaPlaceholder type="video" onUpload={handleUpload} onEmbed={handleEmbed} />
        </MediaBlockOuter>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper data-selected={selected || undefined}>
      <MediaBlockOuter>
        <MediaBlockHandle editor={editor} getPos={getPos} />
        {loading ? (
          <MediaSpinner>동영상 로딩 중...</MediaSpinner>
        ) : (
          <ResizableMedia width={width} onWidthChange={handleWidthChange}>
            {embedUrl ? (
              <iframe
                src={embedUrl}
                title="Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video src={displaySrc!} controls preload="metadata" />
            )}
          </ResizableMedia>
        )}
      </MediaBlockOuter>
    </NodeViewWrapper>
  );
};

/* ================================================================== */
/* AudioBlockView                                                      */
/* ================================================================== */

export const AudioBlockView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
  updateAttributes,
  selected,
}) => {
  const { src, serverPath, fileName } = node.attrs;
  const { blobUrl, loading } = useAuthenticatedMedia(serverPath || null);
  const displaySrc = src || blobUrl;

  const handleUpload = useCallback(
    (info: {
      serverPath: string;
      sessionId: string;
      messageId: string;
      fileName: string;
      fileSize: number;
      mimeType: string;
    }) => {
      updateAttributes({
        serverPath: info.serverPath,
        sessionId: info.sessionId,
        messageId: info.messageId,
        fileName: info.fileName,
        fileSize: info.fileSize,
        mimeType: info.mimeType,
      });
    },
    [updateAttributes],
  );

  const handleEmbed = useCallback(
    (url: string) => {
      updateAttributes({ src: url });
    },
    [updateAttributes],
  );

  if (!displaySrc && !loading) {
    return (
      <NodeViewWrapper data-selected={selected || undefined}>
        <MediaBlockOuter>
          <MediaBlockHandle editor={editor} getPos={getPos} />
          <MediaPlaceholder type="audio" onUpload={handleUpload} onEmbed={handleEmbed} />
        </MediaBlockOuter>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper data-selected={selected || undefined}>
      <MediaBlockOuter>
        <MediaBlockHandle editor={editor} getPos={getPos} />
        <AudioWrap>
          <AudioIcon>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </AudioIcon>
          <AudioBody>
            {fileName && <AudioFileName>{fileName}</AudioFileName>}
            {loading ? (
              <MediaSpinner>오디오 로딩 중...</MediaSpinner>
            ) : (
              <audio src={displaySrc!} controls preload="metadata" />
            )}
          </AudioBody>
        </AudioWrap>
      </MediaBlockOuter>
    </NodeViewWrapper>
  );
};

/* ================================================================== */
/* FileBlockView                                                       */
/* ================================================================== */

export const FileBlockView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
  updateAttributes,
  selected,
}) => {
  const { serverPath, fileName, fileSize, mimeType } = node.attrs;
  const fileType = useMemo(
    () => getFileTypeInfo(fileName, mimeType),
    [fileName, mimeType],
  );

  const handleUpload = useCallback(
    (info: {
      serverPath: string;
      sessionId: string;
      messageId: string;
      fileName: string;
      fileSize: number;
      mimeType: string;
    }) => {
      updateAttributes({
        serverPath: info.serverPath,
        sessionId: info.sessionId,
        messageId: info.messageId,
        fileName: info.fileName,
        fileSize: info.fileSize,
        mimeType: info.mimeType,
      });
    },
    [updateAttributes],
  );

  const handleDownload = useCallback(async () => {
    if (!serverPath) return;
    try {
      const res = await authFetch(`${API_BASE_URL}${serverPath}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[FileBlockView] download failed:', err);
    }
  }, [serverPath, fileName]);

  if (!serverPath) {
    return (
      <NodeViewWrapper data-selected={selected || undefined}>
        <MediaBlockOuter>
          <MediaBlockHandle editor={editor} getPos={getPos} />
          <MediaPlaceholder type="file" onUpload={handleUpload} />
        </MediaBlockOuter>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper data-selected={selected || undefined}>
      <MediaBlockOuter>
        <MediaBlockHandle editor={editor} getPos={getPos} />
        <FileCardWrap>
          <FileIcon style={{ background: fileType.bg, color: fileType.color }}>
            {fileType.icon}
          </FileIcon>
          <FileInfo>
            <FileName>{fileName || 'File'}</FileName>
            <FileMeta>
              {fileType.ext && `${fileType.ext} · `}{formatSize(fileSize)}
            </FileMeta>
          </FileInfo>
          <FileDownloadBtn onClick={handleDownload} title="다운로드">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </FileDownloadBtn>
        </FileCardWrap>
      </MediaBlockOuter>
    </NodeViewWrapper>
  );
};

/* ================================================================== */
/* BookmarkBlockView                                                   */
/* ================================================================== */

/**
 * fetchOgMetadata — 백엔드 프리뷰 API를 통해 OG 메타데이터를 가져온다.
 * (브라우저 CORS 제약 회피)
 */
async function fetchOgMetadata(
  inputUrl: string,
): Promise<{
  title: string | null;
  description: string | null;
  image: string | null;
  favicon: string | null;
}> {
  let hostname = '';
  try {
    hostname = new URL(inputUrl).hostname;
  } catch { /* ignore */ }

  const fallback = {
    title: null,
    description: null,
    image: null,
    favicon: hostname
      ? `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`
      : null,
  };

  try {
    const preview = await previewBookmark(inputUrl, 'document');
    return {
      title: preview.title || null,
      description: preview.description || null,
      image: preview.image || null,
      favicon: preview.favicon || fallback.favicon,
    };
  } catch {
    return fallback;
  }
}

export const BookmarkBlockView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
  updateAttributes,
  selected,
}) => {
  const { url, title, description, favicon, image } = node.attrs;
  const fetchedRef = useRef(false);

  /* URL이 세팅된 후 OG 메타데이터를 한 번만 fetch */
  useEffect(() => {
    if (!url || fetchedRef.current) return;
    if (title && title !== url) return; // 이미 메타데이터가 있으면 스킵
    fetchedRef.current = true;

    fetchOgMetadata(url).then((meta) => {
      const updates: Record<string, string | null> = {};
      if (meta.title) updates.title = meta.title;
      if (meta.description) updates.description = meta.description;
      if (meta.image) updates.image = meta.image;
      if (meta.favicon) updates.favicon = meta.favicon;
      if (Object.keys(updates).length > 0) updateAttributes(updates);
    });
  }, [url, title, updateAttributes]);

  const handleEmbed = useCallback(
    (inputUrl: string) => {
      let faviconUrl: string | null = null;
      try {
        const parsed = new URL(inputUrl);
        faviconUrl = `https://www.google.com/s2/favicons?domain=${parsed.hostname}&sz=32`;
      } catch { /* ignore */ }

      fetchedRef.current = false;
      updateAttributes({
        url: inputUrl,
        title: inputUrl,
        favicon: faviconUrl,
        description: null,
        image: null,
      });
    },
    [updateAttributes],
  );

  if (!url) {
    return (
      <NodeViewWrapper data-selected={selected || undefined}>
        <MediaBlockOuter>
          <MediaBlockHandle editor={editor} getPos={getPos} />
          <MediaPlaceholder type="bookmark" onEmbed={handleEmbed} />
        </MediaBlockOuter>
      </NodeViewWrapper>
    );
  }

  const displayTitle = title || url;
  let displayUrl = url;
  try {
    const parsed = new URL(url);
    displayUrl = parsed.hostname + (parsed.pathname !== '/' ? parsed.pathname : '');
  } catch { /* use raw url */ }

  return (
    <NodeViewWrapper data-selected={selected || undefined}>
      <MediaBlockOuter>
        <MediaBlockHandle editor={editor} getPos={getPos} />
        <BookmarkCardWrap href={url} target="_blank" rel="noopener noreferrer">
          <BookmarkBody>
            <BookmarkTextArea>
              <BookmarkTitle>{displayTitle}</BookmarkTitle>
              {description && <BookmarkDescription>{description}</BookmarkDescription>}
            </BookmarkTextArea>
            <BookmarkMeta>
              {favicon && <BookmarkFavicon src={favicon} alt="" />}
              <BookmarkUrl>{displayUrl}</BookmarkUrl>
            </BookmarkMeta>
          </BookmarkBody>
          {image && (
            <BookmarkImage>
              <img src={image} alt="" />
            </BookmarkImage>
          )}
        </BookmarkCardWrap>
      </MediaBlockOuter>
    </NodeViewWrapper>
  );
};
